import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Award, RotateCcw } from "lucide-react";

export default function QuizPage() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => {
      setCourse(r.data);
      setAnswers(new Array(r.data.quiz?.length || 0).fill(-1));
    });
  }, [id]);

  if (!course) return <div className="min-h-[40vh] grid place-items-center text-sm text-zinc-500">Loading…</div>;
  if (!course.quiz?.length) return <div className="p-10 text-center">This course has no quiz.</div>;

  const submit = async () => {
    if (answers.some((a) => a < 0)) return toast.error("Answer every question first.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/attempt", { course_id: id, answers });
      setResult(data);
      if (data.passed) toast.success(`Passed with ${data.score}% - certificate ready!`);
      else toast.error(`Scored ${data.score}% - try again to pass.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const retake = () => {
    setResult(null);
    setAnswers(new Array(course.quiz.length).fill(-1));
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="eyebrow">{course.title}</div>
      <h1 className="font-display text-4xl mb-6">Final quiz</h1>

      {!result && (
        <div className="space-y-5">
          {course.quiz.map((q, i) => (
            <div key={i} className="card-warm p-5" data-testid={`quiz-q-${i}`}>
              <div className="font-semibold mb-3">{i + 1}. {q.q}</div>
              <div className="grid gap-2">
                {q.options.map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${answers[i] === oi ? "border-[var(--ink)] bg-[var(--muted-surface)]" : "border-[var(--border-warm)] hover:border-zinc-400"}`}>
                    <input
                      type="radio"
                      name={`q-${i}`}
                      checked={answers[i] === oi}
                      onChange={() => { const n = [...answers]; n[i] = oi; setAnswers(n); }}
                      className="accent-[var(--terracotta)]"
                      data-testid={`quiz-q-${i}-opt-${oi}`}
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button
            onClick={submit}
            disabled={submitting}
            className="btn-terracotta px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-70"
            data-testid="quiz-submit-btn"
          >{submitting ? "Grading…" : "Submit answers"}</button>
        </div>
      )}

      {result && (
        <div className="card-warm p-8 text-center" data-testid="quiz-result">
          <Award size={40} className={result.passed ? "mx-auto text-[var(--terracotta)]" : "mx-auto text-zinc-400"}/>
          <h2 className="font-display text-3xl mt-3">
            {result.passed ? "You passed!" : "Not quite yet"}
          </h2>
          <p className="text-zinc-600 mt-2">Score: <b>{result.score}%</b> - {result.correct}/{result.total} correct</p>
          <div className="mt-5 flex items-center justify-center gap-2">
            {result.passed && (
              <Link to="/dashboard" className="btn-ink px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="to-dashboard-btn">
                View certificate
              </Link>
            )}
            <button onClick={retake} className="chip inline-flex items-center gap-1" data-testid="retake-quiz-btn">
              <RotateCcw size={14}/> Retake
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
